import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import "./css/Packs.css";

const SORT_LABELS = {
  title: "Pack",
  categoryLabel: "Category",
  priceUsd: "Price",
  matchingItemCount: "Item Count",
  pricePerItem: "$ / Item",
};

const BROWSE_SORT_LABELS = {
  title: "Pack",
  levelNumber: "Level",
  priceUsd: "Price",
};

function formatUsd(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Unpriced";
  return number.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatCompactNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function sortRows(rows, sortConfig) {
  const direction = sortConfig.direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const key = sortConfig.key;
    const aValue = a[key];
    const bValue = b[key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * direction;
    }

    return String(aValue).localeCompare(String(bValue)) * direction;
  });
}

function getPackTitleParts(title) {
  const rawTitle = String(title || "").trim();
  const match = rawTitle.match(/^(.*?)\s+(?:Lv\.?|Level)\s*(\d+)\s*$/i);
  if (!match) return { baseTitle: rawTitle, levelNumber: null, levelLabel: "-" };
  const levelNumber = Number(match[2]);
  return {
    baseTitle: match[1].trim() || rawTitle,
    levelNumber: Number.isFinite(levelNumber) ? levelNumber : null,
    levelLabel: Number.isFinite(levelNumber) ? `Lv${levelNumber}` : "-",
  };
}

function getSourceFamilyKey(pack) {
  const jsonPath = String(pack?.source?.jsonPath || "");
  const match = jsonPath.match(/^(.*\.pack_p\[\d+\])\[\d+\]$/);
  return match ? match[1] : "";
}

function getPackFamilyKey(pack) {
  const parts = getPackTitleParts(pack.title);
  const sourceFamilyKey = getSourceFamilyKey(pack);
  if (sourceFamilyKey) return sourceFamilyKey;
  return [
    pack.categoryKey || "",
    pack.weekday || "",
    pack.shopSectionKey || "",
    pack.packType ?? "",
    pack.branchType ?? "",
    parts.baseTitle.toLowerCase(),
  ].join("::");
}

function buildPackFamilies(rows) {
  const groups = new Map();
  rows.forEach((pack) => {
    const parts = getPackTitleParts(pack.title);
    const key = getPackFamilyKey(pack);
    const group = groups.get(key) || {
      key,
      title: parts.baseTitle || pack.title,
      categoryKey: pack.categoryKey || "",
      categoryLabel: pack.categoryLabel || "",
      shopSectionKey: pack.shopSectionKey || "",
      shopSectionLabel: pack.shopSectionLabel || "",
      rows: [],
    };
    group.rows.push({
      ...pack,
      baseTitle: parts.baseTitle,
      levelNumber: parts.levelNumber,
      levelLabel: parts.levelLabel,
    });
    groups.set(key, group);
  });

  return [...groups.values()].map((group) => ({
    ...group,
    rows: group.rows.sort((a, b) => {
      if (a.levelNumber !== null && b.levelNumber !== null && a.levelNumber !== b.levelNumber) {
        return a.levelNumber - b.levelNumber;
      }
      if (a.priceUsd !== b.priceUsd) return (a.priceUsd || 0) - (b.priceUsd || 0);
      return String(a.title).localeCompare(String(b.title));
    }),
  }));
}

const Packs = () => {
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("packs");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [levelFilter, setLevelFilter] = useState("all");
  const [packs, setPacks] = useState([]);
  const [browsePacks, setBrowsePacks] = useState([]);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("daily_picks");
  const [selectedWeekday, setSelectedWeekday] = useState("all");
  const [selectedShopSectionKey, setSelectedShopSectionKey] = useState("all");
  const [selectedLimitedTime, setSelectedLimitedTime] = useState("include");
  const [expandedPackFamilies, setExpandedPackFamilies] = useState(() => new Set());
  const [sortConfig, setSortConfig] = useState({ key: "pricePerItem", direction: "asc" });
  const [browseSortConfig, setBrowseSortConfig] = useState({ key: "priceUsd", direction: "asc" });
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [loadingBrowsePacks, setLoadingBrowsePacks] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadingSummary(true);
    axiosInstance.get("/store-catalog/summary")
      .then((response) => {
        if (!cancelled) {
          const nextSummary = response.data?.summary || null;
          setSummary(nextSummary);
          const categories = nextSummary?.filters?.categories || [];
          if (categories.length) {
            setSelectedCategoryKey((current) => (
              categories.some((category) => category.key === current) ? current : categories[0].key
            ));
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError("Pack catalogue summary could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryOptions = useMemo(() => summary?.filters?.categories || [], [summary]);
  const dailyPickWeekdays = useMemo(() => summary?.filters?.dailyPickWeekdays || [], [summary]);
  const normalShopSections = useMemo(() => summary?.filters?.normalShopSections || [], [summary]);

  useEffect(() => {
    if (selectedCategoryKey === "daily_picks" && selectedWeekday === "all" && dailyPickWeekdays.length === 1) {
      setSelectedWeekday(dailyPickWeekdays[0]);
    }
  }, [dailyPickWeekdays, selectedCategoryKey, selectedWeekday]);

  useEffect(() => {
    let cancelled = false;
    const params = {
      categoryKey: selectedCategoryKey,
      limitedTime: selectedLimitedTime,
      limit: 500,
    };
    if (selectedCategoryKey === "daily_picks" && selectedWeekday !== "all") {
      params.weekday = selectedWeekday;
    }
    if (selectedCategoryKey === "normal_shop" && selectedShopSectionKey !== "all") {
      params.shopSectionKey = selectedShopSectionKey;
    }

    setLoadingBrowsePacks(true);
    axiosInstance.get("/store-catalog/packs", { params })
      .then((response) => {
        if (!cancelled) setBrowsePacks(response.data?.packs || []);
      })
      .catch(() => {
        if (!cancelled) {
          setBrowsePacks([]);
          setError("Pack category browse could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingBrowsePacks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategoryKey, selectedLimitedTime, selectedShopSectionKey, selectedWeekday]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setItems([]);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setLoadingItems(true);
      axiosInstance.get("/store-catalog/items/search", { params: { q: trimmed, limit: 30 } })
        .then((response) => {
          if (!cancelled) setItems(response.data?.items || []);
        })
        .catch(() => {
          if (!cancelled) setError("Item search failed.");
        })
        .finally(() => {
          if (!cancelled) setLoadingItems(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  const loadPacksForItem = useCallback((item, nextLevel = "all") => {
    if (!item?.itemGid) return;
    setSelectedItem(item);
    setLevelFilter(nextLevel);
    setLoadingPacks(true);
    setError("");
    axiosInstance.get(`/store-catalog/items/${item.itemGid}/packs`, { params: { level: nextLevel, limitedTime: selectedLimitedTime } })
      .then((response) => {
        setPacks(response.data?.packs || []);
      })
      .catch(() => {
        setPacks([]);
        setError("Packs could not be loaded for that item.");
      })
      .finally(() => {
        setLoadingPacks(false);
      });
  }, [selectedLimitedTime]);

  useEffect(() => {
    if (selectedItem?.itemGid) {
      loadPacksForItem(selectedItem, levelFilter);
    }
    // Only refetch the current item-efficiency table when this cross-view filter changes.
    // Level and item changes already fetch through their own handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLimitedTime]);

  const handleLevelChange = (event) => {
    const nextLevel = event.target.value;
    if (selectedItem) loadPacksForItem(selectedItem, nextLevel);
  };

  const requestSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "matchingItemCount" ? "desc" : "asc" };
    });
  };

  const requestBrowseSort = (key) => {
    setBrowseSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "totalRewardCount" ? "desc" : "asc" };
    });
  };

  const sortedPacks = useMemo(() => sortRows(packs, sortConfig), [packs, sortConfig]);
  const sortedBrowseFamilies = useMemo(() => {
    const families = buildPackFamilies(browsePacks);
    const representativeRows = families.map((family) => ({
      ...family.rows[0],
      family,
      title: family.title,
      categoryLabel: family.categoryLabel,
      shopSectionLabel: family.shopSectionLabel,
    }));
    return sortRows(representativeRows, browseSortConfig).map((row) => row.family);
  }, [browsePacks, browseSortConfig]);
  const browseGroups = useMemo(() => {
    if (selectedCategoryKey !== "normal_shop" || selectedShopSectionKey !== "all") {
      return [{ key: "selected", label: "", families: sortedBrowseFamilies }];
    }
    const groups = new Map();
    sortedBrowseFamilies.forEach((family) => {
      const key = family.shopSectionKey || "other";
      const label = family.shopSectionLabel || "Other";
      const group = groups.get(key) || { key, label, families: [] };
      group.families.push(family);
      groups.set(key, group);
    });
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedCategoryKey, selectedShopSectionKey, sortedBrowseFamilies]);
  const levelOptions = selectedItem?.levels?.length ? selectedItem.levels : [];

  const togglePackFamily = (familyKey) => {
    setExpandedPackFamilies((current) => {
      const next = new Set(current);
      if (next.has(familyKey)) next.delete(familyKey);
      else next.add(familyKey);
      return next;
    });
  };

  return (
    <main className="packs-page">
      <header className="packs-page-head">
        {summary?.generatedAtUtc && (
          <time dateTime={summary.generatedAtUtc}>
            Generated {new Date(summary.generatedAtUtc).toLocaleString()}
          </time>
        )}
        {loadingSummary && <span>Loading catalogue</span>}
      </header>

      <nav className="packs-tabs" aria-label="Packs views">
        <button
          type="button"
          className={activeTab === "packs" ? "is-active" : ""}
          onClick={() => setActiveTab("packs")}
        >
          Packs
        </button>
        <button
          type="button"
          className={activeTab === "search" ? "is-active" : ""}
          onClick={() => setActiveTab("search")}
        >
          Search Item
        </button>
      </nav>

      {error && <div className="packs-error">{error}</div>}

      {activeTab === "packs" && (
        <section className="packs-browser">
          <div className="packs-section-title">
            <div>
              <h2>Pack Browser</h2>
              <span>{loadingBrowsePacks ? "Loading packs" : `${browsePacks.length} packs`}</span>
            </div>
          </div>

          <div className="packs-filter-row">
            <label className="packs-filter-field">
              <span>Category</span>
              <select value={selectedCategoryKey} onChange={(event) => setSelectedCategoryKey(event.target.value)}>
                {categoryOptions.map((category) => (
                  <option key={category.key} value={category.key}>
                    {category.label} ({category.count})
                  </option>
                ))}
              </select>
            </label>

            {selectedCategoryKey === "daily_picks" && (
              <label className="packs-filter-field">
                <span>Day</span>
                <select value={selectedWeekday} onChange={(event) => setSelectedWeekday(event.target.value)}>
                  <option value="all">All days</option>
                  {dailyPickWeekdays.map((weekday) => (
                    <option key={weekday} value={weekday}>{weekday}</option>
                  ))}
                </select>
              </label>
            )}

            {selectedCategoryKey === "normal_shop" && (
              <label className="packs-filter-field">
                <span>Section</span>
                <select value={selectedShopSectionKey} onChange={(event) => setSelectedShopSectionKey(event.target.value)}>
                  <option value="all">All sections</option>
                  {normalShopSections.map((section) => (
                    <option key={section.key} value={section.key}>
                      {section.label} ({section.count})
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="packs-filter-field">
              <span>Event / Limited</span>
              <select value={selectedLimitedTime} onChange={(event) => setSelectedLimitedTime(event.target.value)}>
                <option value="include">Include event/limited</option>
                <option value="exclude">Exclude event/limited</option>
                <option value="only">Only event/limited</option>
              </select>
            </label>
          </div>

          <div className="packs-browse-groups">
            {browseGroups.map((group) => (
              <section className="packs-browse-group" key={group.key}>
                {group.label && (
                  <div className="packs-group-heading">
                    <h3>{group.label}</h3>
                    <span>{group.families.reduce((total, family) => total + family.rows.length, 0)} packs</span>
                  </div>
                )}
                <div className="packs-table-wrap">
                  <table className="packs-table packs-browse-table">
                    <thead>
                      <tr>
                        {Object.entries(BROWSE_SORT_LABELS).map(([key, label]) => (
                          <th key={key}>
                            <button type="button" onClick={() => requestBrowseSort(key)}>
                              {label}
                              {browseSortConfig.key === key && <span>{browseSortConfig.direction === "asc" ? "^" : "v"}</span>}
                            </button>
                          </th>
                        ))}
                        <th>Category</th>
                        <th>Section</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.families.flatMap((family) => {
                        const isExpanded = expandedPackFamilies.has(family.key);
                        const visibleRows = isExpanded ? family.rows : family.rows.slice(0, 1);
                        return visibleRows.flatMap((pack, index) => {
                          const hasMoreLevels = family.rows.length > 1;
                          const rowKey = `${pack.packGid}-${pack.productId}-${pack.source?.jsonPath}`;
                          return [
                            <tr key={`${rowKey}-pack`} className={index > 0 ? "packs-expanded-level-row" : ""}>
                              <td data-label="Pack">
                                <div className="packs-pack-name-cell">
                                  {index === 0 && hasMoreLevels ? (
                                    <button
                                      type="button"
                                      className="packs-level-toggle"
                                      onClick={() => togglePackFamily(family.key)}
                                      aria-expanded={isExpanded}
                                      title={isExpanded ? "Hide additional levels" : "Show additional levels"}
                                    >
                                      <span aria-hidden="true">{isExpanded ? "v" : ">"}</span>
                                      {isExpanded ? "Hide levels" : `Show ${family.rows.length - 1} more`}
                                    </button>
                                  ) : (
                                    <span className="packs-expand-spacer" />
                                  )}
                                  <div>
                                    <strong>{family.title}</strong>
                                    {index === 0 && hasMoreLevels && (
                                      <span className="packs-level-summary">
                                        {isExpanded
                                          ? `${family.rows.length} levels shown`
                                          : `${pack.levelLabel} shown, ${family.rows.length - 1} more available`}
                                      </span>
                                    )}
                                    {index > 0 && <span className="packs-level-summary">Additional level</span>}
                                  </div>
                                </div>
                                {pack.subtitle && <span>{pack.subtitle}</span>}
                              </td>
                              <td data-label="Level">{pack.levelLabel}</td>
                              <td data-label="Price">{formatUsd(pack.priceUsd)}</td>
                              <td data-label="Category">
                                <span className="packs-category-pill">{pack.categoryLabel || "Uncategorized"}</span>
                              </td>
                              <td data-label="Section">{pack.shopSectionLabel || "-"}</td>
                            </tr>,
                            <tr key={`${rowKey}-items`} className="packs-item-breakdown-row">
                              <td colSpan="5">
                                <table className="packs-item-breakdown-table">
                                  <thead>
                                    <tr>
                                      <th>Item</th>
                                      <th>Qty</th>
                                      <th>$ / Item</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(pack.rewardRows || []).map((reward) => (
                                      <tr key={`${rowKey}-${reward.itemGid || reward.name}-${reward.level || 0}`}>
                                        <td>{reward.name}</td>
                                        <td>{formatCompactNumber(reward.count)}</td>
                                        <td>{reward.pricePerItem === null ? "Unpriced" : formatUsd(reward.pricePerItem)}</td>
                                      </tr>
                                    ))}
                                    {!(pack.rewardRows || []).length && (
                                      <tr>
                                        <td colSpan="3" className="packs-empty">No item rewards found.</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </td>
                            </tr>,
                          ];
                        });
                      })}
                      {!loadingBrowsePacks && !group.families.length && (
                        <tr>
                          <td colSpan="5" className="packs-empty">No packs match these filters.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </section>
      )}

      {activeTab === "search" && (
        <>
          <section className="packs-search-band">
            <label className="packs-search-field">
              <span>Item</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Mutagen Source Stone"
              />
            </label>
            {selectedItem && (
              <label className="packs-level-field">
                <span>Level</span>
                <select value={levelFilter} onChange={handleLevelChange}>
                  <option value="all">All levels</option>
                  {levelOptions.map((level) => (
                    <option key={level} value={level}>Level {level}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="packs-level-field">
              <span>Event / Limited</span>
              <select value={selectedLimitedTime} onChange={(event) => setSelectedLimitedTime(event.target.value)}>
                <option value="include">Include event/limited</option>
                <option value="exclude">Exclude event/limited</option>
                <option value="only">Only event/limited</option>
              </select>
            </label>
          </section>

          <section className="packs-layout">
            <aside className="packs-item-results">
              <div className="packs-section-title">
                <h2>Items</h2>
                {loadingItems && <span>Searching</span>}
              </div>
              <div className="packs-item-list">
                {items.map((item) => (
                  <button
                    type="button"
                    key={item.itemGid}
                    className={`packs-item-option ${selectedItem?.itemGid === item.itemGid ? "is-selected" : ""}`}
                    onClick={() => loadPacksForItem(item, "all")}
                  >
                    <strong>{item.name}</strong>
                    <span>{item.packCount} packs</span>
                  </button>
                ))}
                {query.trim().length >= 2 && !loadingItems && !items.length && (
                  <div className="packs-empty">No matching items.</div>
                )}
              </div>
            </aside>

            <section className="packs-results">
              <div className="packs-section-title">
                <div>
                  <h2>{selectedItem ? selectedItem.name : "Pack Efficiency"}</h2>
                  {selectedItem && (
                    <span>{sortedPacks.length} matching packs</span>
                  )}
                </div>
                {loadingPacks && <span>Loading packs</span>}
              </div>

              <div className="packs-table-wrap">
                <table className="packs-table">
                  <thead>
                    <tr>
                      {Object.entries(SORT_LABELS).map(([key, label]) => (
                        <th key={key}>
                          <button type="button" onClick={() => requestSort(key)}>
                            {label}
                            {sortConfig.key === key && <span>{sortConfig.direction === "asc" ? "^" : "v"}</span>}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPacks.map((pack) => (
                      <tr key={`${pack.packGid}-${pack.productId}-${pack.source?.jsonPath}`}>
                        <td>
                          <strong>{pack.title}</strong>
                          {pack.subtitle && <span>{pack.subtitle}</span>}
                        </td>
                        <td>
                          <span className="packs-category-pill">{pack.categoryLabel || "Uncategorized"}</span>
                        </td>
                        <td>{formatUsd(pack.priceUsd)}</td>
                        <td>{formatCompactNumber(pack.matchingItemCount)}</td>
                        <td>{pack.pricePerItem === null ? "Unpriced" : formatUsd(pack.pricePerItem)}</td>
                      </tr>
                    ))}
                    {!selectedItem && (
                      <tr>
                        <td colSpan="5" className="packs-empty">Select an item to view pack efficiency.</td>
                      </tr>
                    )}
                    {selectedItem && !loadingPacks && !sortedPacks.length && (
                      <tr>
                        <td colSpan="5" className="packs-empty">No packs contain this item.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        </>
      )}
    </main>
  );
};

export default Packs;
